import LZString from 'lz-string';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Scenario, SelectedGeo } from './types';

// Deeplink params, mirroring the dashboard's lz-string-compressed-JSON URL
// state (see CustomDash filters' `?filters=`). Two params so the page
// geography and the scenarios can be shared/restored independently.
const GEO_PARAM = 'geo';
const SCENARIOS_PARAM = 'scenarios';

function encode<T>(value: T): string {
  return LZString.compressToEncodedURIComponent(JSON.stringify(value));
}

function decode(raw: string | null): unknown {
  if (!raw) return null;
  const json = LZString.decompressFromEncodedURIComponent(raw);
  if (!json) return null;
  try {
    return JSON.parse(json) as unknown;
  } catch {
    return null;
  }
}

// URL payloads are untrusted (anyone can hand-edit a link), so validate the
// decoded shape before trusting it rather than casting blindly.
function isSelectedGeo(value: unknown): value is SelectedGeo {
  if (typeof value !== 'object' || value === null) return false;
  const geo = value as Record<string, unknown>;
  return (
    (geo.level === 'state' || geo.level === 'county') &&
    typeof geo.fips === 'string' &&
    typeof geo.name === 'string' &&
    typeof geo.stateFips === 'string'
  );
}

function isScenario(value: unknown): value is Scenario {
  if (typeof value !== 'object' || value === null) return false;
  const scenario = value as Record<string, unknown>;
  return (
    typeof scenario.id === 'string' &&
    typeof scenario.industry === 'string' &&
    typeof scenario.industryId === 'string' &&
    typeof scenario.amount === 'number' &&
    isSelectedGeo(scenario.geo)
  );
}

function parseGeo(raw: string | null): SelectedGeo | null {
  const value = decode(raw);
  return isSelectedGeo(value) ? value : null;
}

function parseScenarios(raw: string | null): Scenario[] {
  const value = decode(raw);
  return Array.isArray(value) ? value.filter(isScenario) : [];
}

/**
 * The page's geography + scenarios as deeplinkable state: hydrated once from
 * the URL on mount (so a shared link renders correctly on first paint) and
 * mirrored back into the URL on every change.
 */
export function useEconomicImpactUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedGeo, setSelectedGeo] = useState<SelectedGeo | null>(() =>
    parseGeo(searchParams.get(GEO_PARAM))
  );
  const [scenarios, setScenarios] = useState<Scenario[]>(() =>
    parseScenarios(searchParams.get(SCENARIOS_PARAM))
  );

  useEffect(() => {
    const geoParam = selectedGeo ? encode(selectedGeo) : null;
    const scenariosParam = scenarios.length > 0 ? encode(scenarios) : null;
    setSearchParams(
      (prev) => {
        if (
          prev.get(GEO_PARAM) === geoParam &&
          prev.get(SCENARIOS_PARAM) === scenariosParam
        ) {
          // Nothing changed (incl. the fresh-load default) — leave the URL be.
          return prev;
        }
        const next = new URLSearchParams(prev);
        if (geoParam) next.set(GEO_PARAM, geoParam);
        else next.delete(GEO_PARAM);
        if (scenariosParam) next.set(SCENARIOS_PARAM, scenariosParam);
        else next.delete(SCENARIOS_PARAM);
        return next;
      },
      // Replace so dragging through selections doesn't flood browser history.
      { replace: true }
    );
  }, [selectedGeo, scenarios, setSearchParams]);

  return { selectedGeo, setSelectedGeo, scenarios, setScenarios };
}
